/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Documents oRPC Contract
 *
 * Defines the API contract for document operations including:
 * - Get/save user documents (TLDraw canvas data)
 * - Document metrics recording
 *
 * Documents are stored as part of user profiles and support
 * TLDraw canvas snapshots with camera positions.
 */
import { oc } from "@orpc/contract";
import * as z from "zod";
import { boundedRecord, commonErrors, PhoneE164Schema } from "./base";

// Re-use the E164 schema from base for consistency
const phoneE164Input = PhoneE164Schema;

// ============================================================================
// Shared Schemas
// ============================================================================

/**
 * Camera position for TLDraw editors
 */
export const CameraPositionSchema = z.object({
	x: z.number(),
	y: z.number(),
	z: z.number(),
});

/**
 * TLDraw document schema - canvas content and camera positions
 */
export const TldrawDocumentSchema = z.object({
	type: z.literal("tldraw"),
	/**
	 * TLDraw snapshot is a deeply nested, dynamic structure whose shape is
	 * controlled by the TLDraw library. We constrain the top level to a
	 * record but allow unknown values inside because the internal schema
	 * changes across TLDraw versions and is validated client-side.
	 */
	snapshot: z.record(z.string(), z.unknown()).optional(),
	editorCamera: CameraPositionSchema.nullish(),
	viewerCamera: CameraPositionSchema.nullish(),
	/** Per-page camera positions for the editor (pageId → camera). */
	editorCamerasByPage: z.record(z.string(), CameraPositionSchema).nullish(),
	/** Per-page camera positions for the viewer (pageId → camera). */
	viewerCamerasByPage: z.record(z.string(), CameraPositionSchema).nullish(),
	editorPageId: z.string().nullish(),
	viewerPageId: z.string().nullish(),
	editorStyles: z.record(z.string(), z.unknown()).nullish(),
});

/**
 * Document data schema
 */
export const DocumentDataSchema = z.object({
	version: z.number().int().nonnegative(),
	name: z.string().trim().nullish(),
	age: z.number().int().nonnegative().nullish(),
	document: TldrawDocumentSchema.nullish(),
	metadata: boundedRecord(z.unknown()).nullish(),
});

/**
 * Document response wrapper
 */
export const DocumentResponseSchema = z.object({
	success: z.boolean(),
	data: DocumentDataSchema.nullish(),
	message: z.string().nullish(),
	error: z.string().nullish(),
	code: z.string().nullish(),
});

/**
 * Document metric event types
 */
export const DocumentMetricEventSchema = z.enum([
	"connection_lost_while_dirty",
	"recovery_from_local_storage",
	"recovery_from_trash",
]);

// ============================================================================
// Error Definitions
// ============================================================================

const documentErrors = {
	NOT_FOUND: {
		message: "Document not found",
		data: z.object({ phoneE164: z.string() }),
	},
	UNAUTHORIZED: commonErrors.UNAUTHORIZED,
	VALIDATION_ERROR: commonErrors.VALIDATION_ERROR,
	USER_RESOLUTION_FAILED: {
		message: "Failed to resolve user from phone number",
		data: z.object({ phoneE164: z.string() }),
	},
	SAVE_FAILED: {
		message: "Failed to save document",
		data: z.object({
			phoneE164: z.string(),
			reason: z.string().optional(),
		}),
	},
	RATE_LIMIT_EXCEEDED: {
		message: "Too many save requests",
		data: z.object({
			retryAfterSeconds: z.number(),
		}),
	},
};

// ============================================================================
// Contract Definition
// ============================================================================

export const documentsContract = {
	/**
	 * Get document by phone number
	 *
	 * Retrieves the saved document/profile for a user by their E.164 phone number.
	 */
	getByPhone: oc
		.route({
			method: "GET",
			path: "/api/users/{phoneE164}",
			summary: "Get user document by phone",
			description:
				"Retrieve the saved document/profile for a user by phone number.",
			tags: ["Documents"],
		})
		.input(
			z.object({
				phoneE164: phoneE164Input,
			})
		)
		.output(DocumentResponseSchema)
		.errors(documentErrors),

	/**
	 * Save document
	 *
	 * Update user document/profile fields (partial update).
	 * Server applies rate limiting for document saves.
	 */
	save: oc
		.route({
			method: "PUT",
			path: "/api/users/{phoneE164}",
			summary: "Save user document",
			description:
				"Update user document/profile fields (partial update). Server applies rate limiting for document saves.",
			tags: ["Documents"],
		})
		.input(
			z.object({
				// Path parameter
				phoneE164: phoneE164Input,
				// Body fields (flat structure)
				version: z.number().int().nonnegative().optional(),
				name: z.string().trim().max(200).nullable().optional(),
				age: z.number().int().nonnegative().nullable().optional(),
				document: TldrawDocumentSchema.nullable().optional(),
				metadata: boundedRecord(z.unknown()).nullable().optional(),
				_call_source: z.string().max(50).optional(),
			})
		)
		.output(DocumentResponseSchema)
		.errors(documentErrors),

	// ============================================================================
	// Metrics Endpoints
	// ============================================================================

	/**
	 * Record document metric event
	 *
	 * Record a client-side documents metric event (low-cardinality signal).
	 * Used for tracking document recovery and connection issues.
	 */
	recordMetricEvent: oc
		.route({
			method: "POST",
			path: "/api/metrics/documents/event",
			summary: "Record documents metric event",
			description:
				"Record a client-side documents metric event (low-cardinality signal). Requires an authenticated session.",
			tags: ["Documents", "Metrics"],
		})
		.input(
			z.object({
				event: DocumentMetricEventSchema,
			})
		)
		.output(
			z.object({
				success: z.literal(true),
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			VALIDATION_ERROR: {
				message: "Invalid documents metric event payload",
				data: z.object({
					event: z.string(),
				}),
			},
		}),
};

// ============================================================================
// Type Exports
// ============================================================================

export type DocumentsContract = typeof documentsContract;
export type CameraPosition = z.infer<typeof CameraPositionSchema>;
export type TldrawDocument = z.infer<typeof TldrawDocumentSchema>;
export type DocumentData = z.infer<typeof DocumentDataSchema>;
export type DocumentResponse = z.infer<typeof DocumentResponseSchema>;
export type DocumentMetricEvent = z.infer<typeof DocumentMetricEventSchema>;
